import type { FC, ReactNode } from 'react';
import { useMemo } from 'react';
import {
  Configure,
  InstantSearch,
  SearchBox,
} from 'react-instantsearch-hooks-web';
import styled from 'styled-components';

import { usePoiFilterOptions } from '../../map-data/use-filter-options';
import { algoliaSearchClient } from '../../algolia-search';

const SearchContainer = styled.div`
  position: relative;
`;

interface PoiSearchProps {
  children: ReactNode;
  placeholder: string;
}

export const PoiSearch: FC<PoiSearchProps> = ({ children, placeholder }) => {
  const { filterOptions } = usePoiFilterOptions();
  const poiStudyIndex = process.env.REACT_APP_ALGOLIA_INDEX_POI || '';

  const facetFilters = useMemo(() => {
    const filters: (string | string[])[] = [];

    // handle design filter (can be array or single value)
    if (filterOptions.design) {
      if (Array.isArray(filterOptions.design)) {
        // OR logic for multiple designs
        if (filterOptions.design.length > 0) {
          filters.push(filterOptions.design.map(design => `design:${design}`));
        }
      } else {
        filters.push(`design:${filterOptions.design}`);
      }
    }

    // handle other facet filters
    if (filterOptions.status) {
      filters.push(`status:${filterOptions.status}`);
    }
    if (filterOptions.sex) {
      filters.push(`sex:${filterOptions.sex}`);
    }
    if (filterOptions.riskModellingUsed) {
      filters.push(`riskModellingUsed:${filterOptions.riskModellingUsed}`);
    }
    if (filterOptions.smokersNeverEligible) {
      filters.push(
        `smokersNeverEligible:${filterOptions.smokersNeverEligible}`,
      );
    }
    if (filterOptions.targetOutreach) {
      filters.push(`targetOutreach:${filterOptions.targetOutreach}`);
    }
    if (filterOptions.biomarkersCollected) {
      filters.push(`biomarkersCollected:${filterOptions.biomarkersCollected}`);
    }
    if (filterOptions.smokingCessationOffered) {
      filters.push(
        `smokingCessationOffered:${filterOptions.smokingCessationOffered}`,
      );
    }
    if (filterOptions.useCade) {
      filters.push(`useCade:${filterOptions.useCade}`);
    }
    if (filterOptions.resultsAvailable) {
      filters.push(`resultsAvailable:${filterOptions.resultsAvailable}`);
    }
    if (filterOptions.continent) {
      filters.push(`continent:${filterOptions.continent}`);
    }
    if (filterOptions.politicalRegion) {
      filters.push(`politicalRegion:${filterOptions.politicalRegion}`);
    }

    return filters;
  }, [filterOptions]);

  // Note: startFinish/endFinish are filter option keys, but they map to the 'finishYear' numeric field in Algolia
  const numericFilters = useMemo(() => {
    const filters: string[] = [];

    if (filterOptions.startFinish) {
      filters.push(`finishYear >= ${filterOptions.startFinish}`);
    }
    if (filterOptions.endFinish) {
      filters.push(`finishYear <= ${filterOptions.endFinish}`);
    }

    return filters;
  }, [filterOptions]);

  return (
    <InstantSearch searchClient={algoliaSearchClient} indexName={poiStudyIndex}>
      <Configure
        facetFilters={facetFilters}
        numericFilters={numericFilters.length > 0 ? numericFilters : undefined}
      />
      <SearchContainer>
        <SearchBox placeholder={placeholder} />
        {children}
      </SearchContainer>
    </InstantSearch>
  );
};
